// Curses that can be attached to a run code by a defeated player.
// The next player entering that run code plays with the curse active.
// Winning the run (or reaching a milestone) grants bonus Slop Tokens.

export const CURSES = {
  glasscannon: {
    id: 'glasscannon', name: 'GLASS CANNON',
    art: '🥂', color: '#ff2b2b',
    desc: 'Max HP -30%. Damage dealt +25%.',
    modifiers: { hpMult: 0.7, playerDamageMult: 1.25 },
    tokenBonus: 25,
  },
  broke: {
    id: 'broke', name: 'BROKE ASF',
    art: '💸', color: '#ff007f',
    desc: 'Start with 0 gold. Gold earned -50%.',
    modifiers: { startGold: 0, goldMult: 0.5 },
    tokenBonus: 15,
  },
  smoothbrain: {
    id: 'smoothbrain', name: 'SMOOTHBRAIN',
    art: '🧠', color: '#00e5ff',
    desc: 'Card rewards show only 1 option.',
    modifiers: { rewardCount: 1 },
    tokenBonus: 20,
  },
  lowbattery: {
    id: 'lowbattery', name: 'LOW BATTERY',
    art: '🪫', color: '#ffd700',
    desc: 'Start each combat with -1 ENERGY (min 2).',
    modifiers: { energyDelta: -1 },
    tokenBonus: 30,
  },
  buffed: {
    id: 'buffed', name: 'BUFFED ENEMIES',
    art: '💪', color: '#39ff14',
    desc: 'All enemies have +25% HP and +2 STRENGTH.',
    modifiers: { enemyHpMult: 1.25, enemyStrength: 2 },
    tokenBonus: 30,
  },
  norest: {
    id: 'norest', name: 'NO REST FOR THE SLOP',
    art: '☕', color: '#ff007f',
    desc: 'Rest nodes only heal, no upgrades.',
    modifiers: { restNoUpgrade: true },
    tokenBonus: 20,
  },
  // Premium (marketplace) curses - stored inline so client-side lookup works
  mp_glass_reaper: {
    id: 'mp_glass_reaper', name: 'GLASS REAPER', tagline: 'You die. But you slap.',
    art: '☠️', color: '#ff2b2b', premium: true,
    desc: '-50% max HP. +60% damage dealt.',
    modifiers: { hpMult: 0.5, playerDamageMult: 1.6 }, tokenBonus: 60,
  },
  mp_speedrun: {
    id: 'mp_speedrun', name: 'SPEEDRUN OR DIE', tagline: 'Tick tock, gooner.',
    art: '⏱️', color: '#ffd700', premium: true,
    desc: 'Enemies gain +2 STR every turn. You draw +1 each turn.',
    modifiers: { enemyStrengthPerTurn: 2, extraDrawEach: 1 }, tokenBonus: 75,
  },
  mp_broke_gigachad: {
    id: 'mp_broke_gigachad', name: 'BROKE GIGACHAD', tagline: 'No gold. No relics. Pure aura.',
    art: '💪', color: '#39ff14', premium: true,
    desc: '0 gold ever. +3 max energy. Enemies +40% HP.',
    modifiers: { startGold: 0, goldMult: 0, energyDelta: 3, enemyHpMult: 1.4 }, tokenBonus: 100,
  },
  mp_double_dip: {
    id: 'mp_double_dip', name: 'DOUBLE DIP', tagline: 'Twice the slop, twice the pain.',
    art: '🥣', color: '#ff007f', premium: true,
    desc: '+80% enemy HP. 3x tokens if won.',
    modifiers: { enemyHpMult: 1.8 }, tokenBonus: 120,
  },
  mp_locked_in: {
    id: 'mp_locked_in', name: 'LOCKED IN', tagline: 'No choices. Just vibes.',
    art: '🔒', color: '#00e5ff', premium: true,
    desc: 'Card rewards: 1 card only, no skip.',
    modifiers: { rewardCount: 1, noSkipReward: true }, tokenBonus: 50,
  },
  mp_terminally_online: {
    id: 'mp_terminally_online', name: 'TERMINALLY ONLINE', tagline: 'Log off, king.',
    art: '📱', color: '#7c3aed', premium: true,
    desc: 'Enemies apply +2 VULN at start. You gain +6 max HP.',
    modifiers: { enemyApplyVulnStart: 2, hpDelta: 6 }, tokenBonus: 65,
  },
};

export const CURSE_LIST = Object.keys(CURSES).filter(id => !CURSES[id].premium);
export const PREMIUM_CURSE_LIST = Object.keys(CURSES).filter(id => CURSES[id].premium);
export function curseById(id) { return CURSES[id]; }

// v3.18 STACKED CURSES: allow the SAME curse to stack repeatedly (up to MAX_CURSE_STACK
// per run) and clamp the merged modifier bag so extreme stacks stay playable.
export const MAX_CURSE_STACK = 100;

// Clamp bounds: no matter how many copies of a curse are stacked, the game must remain
// technically playable. These floors/ceilings kick in only when the compound math would
// exceed sane limits.
const MOD_CLAMP = {
  hpMult:            { min: 0.05, max: 100 },   // 5% max HP floor  (~-95%)
  playerDamageMult:  { min: 0.1,  max: 25 },    // 25x damage ceiling
  enemyHpMult:       { min: 0.1,  max: 25 },
  goldMult:          { min: 0,    max: 10 },
  restGoldMult:      { min: 0,    max: 10 },
  // additive numeric caps
  energyDelta:       { min: -3,   max: 10 },
  enemyStrength:     { min: 0,    max: 25 },
  enemyStrengthPerTurn: { min: 0, max: 8 },
  enemyApplyVulnStart:  { min: 0, max: 6 },
  extraDrawEach:     { min: 0,    max: 5 },
  hpDelta:           { min: -60,  max: 60 },
  rewardCount:       { min: 1,    max: 6 },
};

function clampMod(k, v) {
  const c = MOD_CLAMP[k];
  if (!c) return v;
  if (typeof v !== 'number') return v;
  return Math.min(c.max, Math.max(c.min, v));
}

// v3.10 → v3.18 STACKED CURSES: merge modifiers from multiple curses into one modifier bag.
// - Keys ending in "Mult" → compound multiply (and clamp)
// - Booleans → OR
// - `rewardCount`, `startGold` → keep the HARSHEST value (min)
// - Other numeric additives (Delta, Strength, extra*, enemyApplyVulnStart) → sum (and clamp)
export function mergeCurseModifiers(curseIds) {
  const merged = {};
  const MIN_KEYS = new Set(['rewardCount', 'startGold']);
  for (const cid of (curseIds || [])) {
    const mods = CURSES[cid]?.modifiers;
    if (!mods) continue;
    for (const [k, v] of Object.entries(mods)) {
      if (k.endsWith('Mult')) {
        merged[k] = (merged[k] == null ? 1 : merged[k]) * v;
      } else if (typeof v === 'boolean') {
        merged[k] = merged[k] || v;
      } else if (MIN_KEYS.has(k)) {
        merged[k] = (merged[k] == null) ? v : Math.min(merged[k], v);
      } else if (typeof v === 'number') {
        merged[k] = (merged[k] || 0) + v;
      } else {
        merged[k] = v;
      }
    }
  }
  // Clamp every numeric key to bounds so 100× GLASS CANNON doesn't nuke the game.
  for (const k of Object.keys(merged)) {
    merged[k] = clampMod(k, merged[k]);
  }
  return merged;
}

// v3.10 → v3.18 STACKED CURSES: growing token bonus. Each subsequent curse in the stack scales the reward.
// Layer N gets a 1 + N*0.5 multiplier on that curse's base tokenBonus (1×, 1.5×, 2×, 2.5×, ...).
// Extra big-stack milestones (v3.18): every 10 layers past the first grant a flat celebratory bonus.
export function stackedTokenBonus(curseIds) {
  let total = 0;
  const ids = curseIds || [];
  for (let i = 0; i < ids.length; i++) {
    const c = CURSES[ids[i]];
    if (!c) continue;
    const mult = 1 + i * 0.5;
    total += Math.floor((c.tokenBonus || 0) * mult);
  }
  // v3.18: milestone bonuses at 10 / 25 / 50 / 100 layers
  if (ids.length >= 10)  total += 250;
  if (ids.length >= 25)  total += 750;
  if (ids.length >= 50)  total += 2000;
  if (ids.length >= 100) total += 5000;
  return total;
}

// v3.10 → v3.18 STACKED CURSES: overall run-victory token multiplier.
// 0c=1×, 1c=2×, then +0.5× per curse. Capped at MAX_CURSE_STACK layers.
export function stackedRunTokenMult(curseCount) {
  if (!curseCount || curseCount <= 0) return 1;
  const c = Math.min(MAX_CURSE_STACK, curseCount);
  return 2 + Math.max(0, c - 1) * 0.5;
}

// v3.18 STACKED CURSES: given the raw curse list, return a Map<curseId, count>
// so the UI can show "GLASS CANNON ×5" instead of five identical chips.
export function tallyCurseStack(curseIds) {
  const tally = new Map();
  for (const cid of (curseIds || [])) {
    if (!CURSES[cid]) continue;
    tally.set(cid, (tally.get(cid) || 0) + 1);
  }
  return tally;
}
